import * as React from "react";
import { ProfileImage, useAlert, WidgetWrapper } from "uxp/components";
import { IAction } from "../../../common/action-editor/ActionEditor";
import { addToPath, IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from "../../../common/common";
import { SelectableItem, selectUIProps } from "../../../common/SelectableItem";
import { ContainerProperty, extractContainerProperties } from "../../../common/ui-props/ContainerProperty";
import { DataSourceProperty } from "../../../common/ui-props/DataSourceProperty";
import { extractTextProperties, TextProperty } from "../../../common/ui-props/TextProperty";
import { IItemListProps, ListProperty, PropertyEditor } from "./ListProperty";
import { appendRowToContextData, IContextData, useInitialContextData } from "../../../../hooks/useContextData";
import { executeAndBind, IDataSource, IDataSourceBinding, processInitialSource, getDependencyParameters } from "../../../common/data-source-picker/datasource";
import { hasValue, parseValue, replaceWithData } from "../../../../util";
import { ICFButton } from "../../../common/ui-fields/item-card-field/ItemCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { generateIconProp } from "../../../common/icon-selector/IconEditor";
import classNames from "classnames";
import MemorizedList from "./MemorizedList";
import ActionRenderComponent from "../../../common/components/ActionRenderComponent";
import WDFilterPanel from "../../../common/components/WDFilterPanel";
import './ItemList.scss'


const XPR: React.ForwardRefExoticComponent<React.RefAttributes<IWidgetTemplateInstanceProps> & IWidgetTemplateProps> = React.forwardRef((props, ref) => {
    const { designer } = props;
    let uiProps = props?.uiProps;

    const [contextData, setContextData] = React.useState<IContextData>({})
    const [actionData, setActionData] = React.useState<{ action: IAction, contextData: IContextData }>({ action: null, contextData: {} })

    const uiPropsRef = React.useRef<any>({})
    const containerRef = React.useRef(null)

    const defaults: IItemListProps = {
        usePaginations: false,
        max: 15,
        profileImageField: '',
        profileNameField: 'userName',
        titleField: 'Sample title',
        subTitleField: 'Sample subtitle',
        dateField: 'createdDate',
        statusField: 'status',
        status: [
            { status: 'Open', color: '#F5800B' },
            { status: 'InProgress', color: '#1077E6' },
            { status: 'Done', color: '#06A70F' },
        ],
        onClickRow: null,
    }
    const XPRProps: IUIPropertyDefinitionMap = {
        "headerContainer": ContainerProperty({
            background: {
                color: "",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            }
        }),
        'title': TextProperty({ text: '', color: '', size: '' }, 'Title'),
        'source': DataSourceProperty('Source', uiProps.source, uiProps.datasources, props.uxpContext, props.designer, 'dictionary-array'),
        'listProps': ListProperty('Item List', uiProps.listProps || defaults)
    }

    let { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)
    let { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'Item List')
    let { max, usePaginations, profileImageField, profileNameField, titleField, subTitleField, dateField, statusField, status: statusMap, onClickRow } = uiProps?.listProps as IItemListProps || defaults
    if (!max) max = 10
    let buttons: ICFButton[] = uiProps?.listProps?.buttons || [];

    const { isLoading, initialContextData } = useInitialContextData(props, [])

    React.useEffect(() => {
        if (!isLoading) {
            setContextData(initialContextData);
        }
    }, [isLoading, initialContextData])

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        return () => {
            designer?.removeErrors?.(['datasource', 'fields'])
        }
    }, [])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])

    React.useEffect(() => {

        processInitialSource(props.designer, props.uxpContext, props.uiProps, props.initialSource, 'dictionary-array')
            .then(res => {
                if (res) props.designer?.setUIProps(res)
            })
            .catch(e => {
                console.log('exception ', e);

            })

    }, [props.initialSource]);

    React.useEffect(() => {
        uiPropsRef.current = uiProps
    }, [uiProps])

    React.useEffect(() => {
        console.log('Action data', actionData);
    }, [actionData])

    React.useEffect(() => {
        console.log('initialdata__contextdata', contextData)
    }, [contextData])

    // this will be called before save
    function validateBeforeSave() {
        if (!isDataSourceValid()) return false
        if (!hasFieldsConfigured()) return false
        return true
    }

    function isDataSourceValid() {
        if (!hasValue(uiProps?.source) || !hasValue(uiProps.source?.source) || !hasValue(uiProps.source?.path)) {
            designer?.addErrors?.([{
                id: 'datasource',
                type: 'property',
                error: 'Data source is required',
                containerId: 'chart-container',
                fieldId: 'source'
            }])
            return false
        }
        designer?.removeErrors?.(['datasource'])
        return true
    }

    function hasFieldsConfigured() {
        if (!hasValue(uiProps?.listProps?.titleField) && !hasValue(uiProps?.listProps?.subTitleField) && !hasValue(uiProps?.listProps?.dateField) && !hasValue(uiProps?.listProps?.statusField)) {

            designer?.addErrors?.([{
                id: 'fields',
                type: 'property',
                error: 'Fields are required',
                containerId: 'chart-container',
                fieldId: 'fields'
            }])

            return false
        }
        designer?.removeErrors?.(['fields'])
        return true
    }

    const getData = React.useCallback(async (max: number, lastToken?: string) => {

        if(!contextData || Object.keys(contextData).length == 0) {
            console.log('skipping triggering the data function because contextdata is not ready')
            return {items:[], pageToken: null}
        }
        return new Promise<{ items: any[], pageToken: string }>(async (done, nope) => {

            // if data source not configured 
            if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {

                // return dummy data 
                let items: any[] = []

                let dummyDataItem: any = {
                    "title": "New Task",
                    "description": "complete the new task",
                    "user": "Jacob Lopez",
                    "profileImage": "",
                    "status": "closed",
                    "date": "2022-07-22 10:22:20"
                }

                if (Object.keys(statusMap).length == 0) {
                    items.push({ ...dummyDataItem })
                }

                for (let s of statusMap) {
                    let _item = { ...dummyDataItem }
                    _item.status = s.status
                    items.push(_item)
                }

                done({ items, pageToken: '1' })
                return
            }

            let params: any = { filters: contextData?.filters || {} }

            if (usePaginations) {
                params['last'] = lastToken || undefined;
                params['max'] = Number(max);
            }
            executeAndBind(props.uxpContext, uiProps.source, uiProps.datasources, params, contextData)
                .then(res => {
                    let items = res;

                    if (!Array.isArray(items)) items = [items]

                    let pageToken = usePaginations ? (lastToken + res.length) : '1'
                    if (res.length < max) { pageToken = '1' }

                    done({ items, pageToken })
                })
                .catch(e => {
                    done({ items: [], pageToken: lastToken })
                })
        })
    }, [props.designer?.mode, contextData])

    const renderItem = React.useCallback((item: any, key: number) => {

        function getStatusColor(item: any) {
            let statusValue = hasValue(statusField) ? replaceWithData(statusField, { ...contextData, row: item }) : null
            if (!statusValue) return null
            let statusInfo = statusMap.find((s: any) => s.status == statusValue)
            if (statusInfo && statusInfo.color) {
                return statusInfo.color
            }
            return null
        }

        function getProps(field: string) {
            let align = 'left'
            if (field == 'status' || field == 'date') align = 'right'
            return [
                {
                    id: addToPath(['itemConfiguration'], field),
                    value: uiProps?.itemConfiguration?.[field] || {},
                    definition: { defaults: { color: '', size: '12px', align: align }, render: PropertyEditor }
                }
            ]
        }

        function onChangeProps(updatedProps: any, field: string) {
            let _itemConfig: any = { ...uiPropsRef.current?.itemConfiguration || {} }
            let align = 'left'
            if (field == 'status' || field == 'date') align = 'right'

            if (!_itemConfig) _itemConfig = {}
            if (!_itemConfig[field]) _itemConfig[field] = { color: '', size: '12px', align: align, useStatusColor: false }
            _itemConfig[field] = { ..._itemConfig[field], ...updatedProps }

            props?.designer?.setUIProps(Object.assign({}, uiProps, { itemConfiguration: _itemConfig }))
        }

        function getFieldStyles(field: string, _status: string) {
            if (!statusMap) return {}
            let styles: React.CSSProperties = {}

            if (uiProps.itemConfiguration && statusMap && uiProps.itemConfiguration) {
                let config = uiProps.itemConfiguration
                if (config[field]) {
                    let { color, size, align, useStatusColor } = config[field]

                    if (hasValue(color)) styles.color = color
                    if (useStatusColor) {
                        // get status 
                        let state = statusMap.find(s => s.status == _status)
                        if (state) styles.color = state.color
                    }
                    if (hasValue(size)) styles.fontSize = size
                    if (hasValue(align)) styles.textAlign = align as any
                }
            }

            return styles
        }

        function getParsedValue(data: any, value: string, defaultFieldName: string) {
            if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
                return data?.row?.[defaultFieldName] || ''
            }

            if (!hasValue(value)) return ''
            return replaceWithData(value, data) || ''
        }

        function handleRowClick(e: React.MouseEvent, item: any) {
            e.preventDefault()
            if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
                return
            }
            setActionData({ action: onClickRow, contextData: { ...contextData, row: item } })
        }

        function makeButton(button: ICFButton, item: any) {
            return <div onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
                    return
                }
                setActionData({ action: button.onClick, contextData: { ...contextData, row: item } })
            }} className='il-card-action-icon' style={{ backgroundColor: button.backgroundColor }}>
                <FontAwesomeIcon icon={generateIconProp(button.icon)} color={button.color} />
            </div>;
        }

        const statusValue = hasValue(statusField) ? parseValue(`{$.${statusField}}`, item) : null
        return <div className={classNames("item-row", { 'clickable': !!onClickRow && !!onClickRow.type })} onClick={e => handleRowClick(e, item)}>

            {(hasValue(profileImageField) || hasValue(profileNameField)) &&
                <div className="user-profile">
                    <ProfileImage
                        image={getParsedValue({ ...contextData, row: item }, profileImageField, 'profileImage') || ''}
                        name={getParsedValue({ ...contextData, row: item }, profileNameField, 'user') || ''}
                    />
                </div>
            }
            <div className="left">
                {hasValue(titleField) &&
                    <SelectableItem
                        designer={props.designer}
                        props={getProps('title')}
                        onChange={(updated) => onChangeProps(updated, 'title')}
                    >
                        <div className="title" style={getFieldStyles('title', statusValue)} >
                            {getParsedValue({ ...contextData, row: item }, titleField, 'title')}
                        </div>
                    </SelectableItem>
                }
                {hasValue(subTitleField) &&
                    <SelectableItem
                        designer={props.designer}
                        props={getProps('subTitle')}
                        onChange={(updated) => onChangeProps(updated, 'subTitle')}
                    >
                        <div className="sub-title" style={getFieldStyles('subTitle', statusValue)}>
                            {getParsedValue({ ...contextData, row: item }, subTitleField, 'description')}
                        </div>
                    </SelectableItem>
                }
            </div>
            <div className="right">

                {hasValue(dateField) &&
                    <SelectableItem
                        designer={props.designer}
                        props={getProps('date')}
                        onChange={(updated) => onChangeProps(updated, 'date')}
                    >
                        <div className="date" style={getFieldStyles('date', statusValue)}>{getParsedValue({ ...contextData, row: item }, dateField, 'date')}</div>
                    </SelectableItem>

                }
                {hasValue(statusField) &&
                    <SelectableItem
                        designer={props.designer}
                        props={getProps('status')}
                        onChange={(updated) => onChangeProps(updated, 'status')}
                    >
                        <div className="status" style={getFieldStyles('status', statusValue)}>{getParsedValue({ ...contextData, row: item }, statusField, 'status')}</div>
                    </SelectableItem>
                }

            </div>

            {(buttons.length > 0) && <div className='il-card-buttons'>
                {
                    buttons.map((b) => makeButton(b, item))
                }
            </div>
            }

            <div className="status-indicator" style={{ backgroundColor: getStatusColor(item) }} ></div>
        </div>
    }, [statusField, contextData, statusMap, profileImageField, profileNameField, titleField, subTitleField, dateField, onClickRow, uiProps, props, buttons

    ])

    function getTitle(title: string, data: any) {
        if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
            return title
        }
        if (!hasValue(title)) return ''
        return replaceWithData(title, data) || ''
    }
    return <WidgetWrapper className="uxp-item-list-card-template">

        <div className="container" ref={containerRef}>
            <div className="header">
                <SelectableItem
                    designer={props.designer}
                    props={selectUIProps(uiProps, XPRProps, 'headerContainer', 'title')}
                    contextData={contextData}
                >
                    <div className="uxp-widget-title-bar uxp-drag-handler" style={headerStyles}>
                        <div className="title-section" style={titleStyles}>
                            <span className="title-icon"></span>
                            <span className="txt">{getTitle(title, contextData)}</span>
                        </div>
                        <div className="action-section">
                            <WDFilterPanel
                                designer={props.designer}
                                uxpContext={props.uxpContext}
                                uiProps={uiProps}
                                containerRef={containerRef}
                                data={contextData || {}}
                                onChangeFilters={(data) => {
                                    setContextData(prev => ({ ...prev, filters: data?.filters || {} }))
                                }}
                                onClearFilters={() => {
                                    setContextData(prev => ({ ...prev, filters: {} }))
                                }}
                            />
                        </div>
                    </div>
                </SelectableItem>
            </div>
            <div className="content">
                <SelectableItem
                    designer={props.designer}
                    props={selectUIProps(uiProps, XPRProps, 'source', 'listProps')}
                    contextData={appendRowToContextData(contextData, props, props?.uiProps?.source)}
                    elementId="chart-container"
                >
                    <MemorizedList
                        data={getData}
                        pageSize={usePaginations ? max : undefined}
                        renderItem={renderItem}
                        className="wd-item-list-template-data-list"
                    />
                </SelectableItem>
            </div>
        </div>

        <ActionRenderComponent
            designer={props.designer}
            uxpContext={props.uxpContext}
            {...actionData}
            onClose={() => setActionData({ action: null, contextData: {} })}
            onReloadWidget={() => { }}
        />
    </WidgetWrapper>
})


registerCardTemplate({
    id: 'item-list',
    name: "Item List",
    description: 'Show a list of items. The list can handle a large number of items, incrementally loading them as you scroll. Each item can have a title, subtitle, status and more.',
    template: XPR,
    icon: ['fas', 'th-list'],
    complexity: 'hard',
    canDragDropFields: true,
    expectedSchema: 'dictionary-array',
});